// Power-on: a short BIOS-style self test with real detected values, then the
// branded loading screen from the approved artwork, then the desktop reveal.
// Click or any key skips straight to the desktop.

import { useEffect, useRef, useState } from 'react';
import { Mark, Wordmark } from '../brand/brand';
import { api } from '../lib/api';
import { detect, gpuLabel } from '../services/hw';

type Line = { k?: string; v: string; cls?: string };

export function Boot({ onDone }: { onDone: () => void }) {
  const [lines, setLines] = useState<Line[]>([]);
  const [stage, setStage] = useState<'bios' | 'brand'>('bios');
  const [progress, setProgress] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const done = useRef(false);

  const finish = () => {
    if (done.current) return;
    done.current = true;
    setLeaving(true);
    setTimeout(onDone, 420);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab') return;
      finish();
    };
    window.addEventListener('keydown', onKey);
    let alive = true;
    const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
    const add = async (l: Line, ms = 110) => {
      if (!alive || done.current) return;
      setLines((x) => [...x, l]);
      await wait(ms);
    };
    (async () => {
      const hwP = detect();
      const health = api('/v1/health', { timeoutMs: 2500 }).then(
        () => true,
        () => false,
      );
      await add({ v: 'NimoOS BIOS 1.3', cls: 'hi' }, 160);
      await add({ v: 'power-on self test', cls: 'k' }, 200);
      const hw = await hwP;
      await add({ k: 'CPU', v: hw.cores ? `${hw.cores} logical cores` : 'not reported' });
      await add({ k: 'MEMORY', v: hw.memoryGb ? `${hw.memoryGb} GB hint from the browser` : 'not reported' });
      await add({ k: 'GPU', v: gpuLabel(hw) });
      await add({ k: 'WEBGPU', v: hw.webgpu ? 'available' : 'absent: CPU mode', cls: hw.webgpu ? 'ok' : 'warn' });
      await add({ k: 'DISPLAY', v: `${hw.screen} @${hw.dpr}x` });
      await add({ k: 'SYSTEM', v: `${hw.os} · ${hw.browser}` });
      const online = await health;
      await add({ k: 'NETWORK', v: online ? 'coordinator online' : 'coordinator unreachable: local apps still work', cls: online ? 'ok' : 'bad' }, 260);
      if (!alive || done.current) return;
      setStage('brand');
      for (let i = 1; i <= 20 && alive && !done.current; i++) {
        setProgress(i / 20);
        await wait(36);
      }
      await wait(200);
      if (alive) finish();
    })();
    return () => {
      alive = false;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={'boot' + (leaving ? ' leaving' : '')} onClick={finish} role="status" aria-live="polite" aria-label="NimoOS is starting">
      {stage === 'brand' && (
        <div className="boot-center">
          <div className="boot-halo">
            <Mark size={168} title="NimoOS" />
          </div>
          <Wordmark height={58} className="boot-wordmark" />
          <div className="boot-status">Starting your workspace...</div>
          <div className="boot-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
            <i style={{ width: `${Math.max(8, progress * 100)}%` }} />
          </div>
        </div>
      )}
      <pre className={'boot-bios' + (stage === 'brand' ? ' gone' : '')} aria-hidden={stage === 'brand'} style={stage === 'brand' ? { pointerEvents: 'none' } : undefined}>
        {lines.map((l, i) => (
          <div key={i}>
            {l.k ? <span className="k">{(l.k + ' ').padEnd(12, '.')} </span> : null}
            <span className={l.cls}>{l.v}</span>
          </div>
        ))}
      </pre>
      <span className={'boot-skip' + (stage === 'bios' ? ' dark' : '')}>click or press any key to skip</span>
    </div>
  );
}

export function ShutdownScreen({ onRestart }: { onRestart: () => void }) {
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => btn.current?.focus(), []);
  return (
    <div className="shutdown" role="alertdialog" aria-labelledby="sd-title">
      <Mark size={120} />
      <h1 id="sd-title">NimoOS has shut down.</h1>
      <p>Mining and active AI responses were stopped. Your chats and assistants are saved to your wallet.</p>
      <p>It is now safe to close this tab.</p>
      <button ref={btn} className="btn primary" onClick={onRestart} style={{ marginTop: 8 }}>
        Start NimoOS
      </button>
    </div>
  );
}
